/**
 * Season cards: the Admissions Season kick-off and the end-of-season thank-you (Peter, 2026-11-04).
 * Kick-off: blue and white confetti from both bottom corners, then the card rises in with a bounce.
 * Thank-you: blue, red and gold fireworks behind a navy card, then gold stars drifting down.
 */
import { api } from "./api";
import { MONO, esc } from "./ui";
import type { SeasonCardShow } from "../shared/types";

const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
const para = (s: string) =>
  s
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

let open = false;

type Card = Omit<SeasonCardShow, "kind"> & { kind: SeasonCardShow["kind"] | "paom"; sig?: string; kick?: string };

/** Shows a card. Real (not preview) cards are marked seen as soon as they appear, so they only ever show once. */
export function showSeasonCard(card: SeasonCardShow, preview = false) {
  if (open) return;
  if (!preview) void api("/api/me/card-seen", { body: { kind: card.kind } }).catch(() => {});
  present(card, preview);
}

/**
 * The PA of the Month thank-you (Peter, 2026-11-03/04): the logo flips over to the card, and hearts and
 * stars keep rising until it's closed. `markSeen` is false for the winner's replay from Home.
 */
export function showPaomThanks(award: { month: string; label: string }, first: string, markSeen: boolean, preview = false) {
  if (open) return;
  if (markSeen && !preview) void api("/api/me/paom-seen", { body: { month: award.month } }).catch(() => {});
  present(
    {
      kind: "paom",
      kick: "Parent Ambassador of the Month",
      title: `Thank you, ${first}!`,
      message: `You're our Parent Ambassador of the Month for ${award.label}.\n\nFamilies remember the warm welcome you give them. We're so grateful for your time and heart.`,
      sig: "With thanks, the MCDS Admissions Team",
      button: "Close",
      stat: null,
    },
    preview,
  );
}

function present(card: Card, preview: boolean) {
  open = true;
  const wrap = document.createElement("div");
  wrap.className = `scard-wrap ${card.kind}`;
  const statLine =
    card.kind === "thanks"
      ? `<div class="scard-stat">${card.stat ? `You helped with ${card.stat} events this season 💙` : "Thank you for being part of this season 💙"}</div>`
      : "";
  // Every card starts as the school logo, then flips over to reveal the card (Peter, 2026-11-04).
  const flip = true;
  wrap.innerHTML = `<canvas aria-hidden="true"></canvas>
  ${flip ? `<div class="scard-flip"><div class="scard-inner"><div class="scard-front" aria-hidden="true">${MONO(132)}</div>` : ""}
  <div class="scard" role="dialog" aria-modal="true" aria-labelledby="scard-t">
    ${
      card.kind === "kickoff" || card.kind === "paom"
        ? `<div class="scard-band">${MONO(44)}<div class="kick">${esc(card.kick || "Ambassador Scheduling")}</div></div><div class="scard-stripe"></div><div class="scard-body">${
            card.kind === "paom" ? '<div class="scard-ribbon" aria-hidden="true">♥</div>' : '<div class="scard-emoji" aria-hidden="true">🎉</div>'
          }`
        : `<div class="scard-body"><div class="scard-star" aria-hidden="true">★</div>`
    }
    <h2 id="scard-t">${esc(card.title)}</h2>${statLine}<div class="scard-msg${card.kind === "paom" ? " center" : ""}">${para(card.message)}</div>${card.sig ? `<p class="scard-sig">${esc(card.sig)}</p>` : ""}
    <button class="btn ${card.kind === "kickoff" ? "block" : "sm scard-close"}" data-scard-close>${esc(card.button)}</button></div>
  </div>${flip ? "</div></div>" : ""}`;
  document.body.appendChild(wrap);
  const canvas = wrap.querySelector("canvas")!;
  const still = reduced();
  const FLIP_AT = 875; // after the logo rises in (halved from 1750, Peter 2026-11-04)
  const stop = still
    ? () => {}
    : card.kind === "kickoff"
      ? confettiCannons(canvas, FLIP_AT + 450)
      : card.kind === "paom"
        ? risingHearts(canvas, FLIP_AT + 300)
        : fireworksThenStars(canvas, FLIP_AT + 450);
  requestAnimationFrame(() => wrap.classList.add("on"));
  const inner = wrap.querySelector<HTMLElement>(".scard-inner");
  if (inner) {
    if (still) inner.classList.add("flipped", "now");
    else setTimeout(() => inner.classList.add("flipped"), FLIP_AT);
  }
  const btn = wrap.querySelector<HTMLButtonElement>("[data-scard-close]")!;
  setTimeout(() => btn.focus(), flip && !still ? FLIP_AT + 800 : 400);
  const close = () => {
    stop();
    document.removeEventListener("keydown", onKey);
    wrap.classList.remove("on");
    setTimeout(() => wrap.remove(), 300);
    open = false;
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };
  btn.addEventListener("click", close);
  document.addEventListener("keydown", onKey);
}

/* ---------------- effects ---------------- */

type P = { x: number; y: number; vx: number; vy: number; r: number; vr: number; c: string; s: number; life: number; shape: number };

function stage(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d")!;
  const dpr = Math.min(devicePixelRatio || 1, 2);
  const fit = () => {
    canvas.width = innerWidth * dpr;
    canvas.height = innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  fit();
  addEventListener("resize", fit);
  return { ctx, unfit: () => removeEventListener("resize", fit) };
}

function loop(canvas: HTMLCanvasElement, step: (ctx: CanvasRenderingContext2D, t: number) => boolean) {
  const { ctx, unfit } = stage(canvas);
  let raf = 0;
  const t0 = performance.now();
  const tick = (now: number) => {
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    if (step(ctx, now - t0)) raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelAnimationFrame(raf);
    unfit();
  };
}

/** Two bursts of blue, white and a little red from both bottom corners, repeating with a 2s pause until the card is closed (Peter, 2026-11-04). */
function confettiCannons(canvas: HTMLCanvasElement, delay = 0) {
  const colors = ["#1600FF", "#1600FF", "#2654F9", "#9DB8FF", "#FFFFFF", "#FFFFFF", "#E10600"];
  const ps: P[] = [];
  const fire = (n: number) => {
    for (const side of [0, 1]) {
      for (let i = 0; i < n; i++) {
        const vx = (3 + Math.random() * 8) * (side ? -1 : 1); // inward
        const vy = -(13 + Math.random() * 10); // up
        ps.push({
          x: side ? innerWidth + 10 : -10, y: innerHeight + 10, vx, vy,
          r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.4, c: colors[(Math.random() * colors.length) | 0],
          s: 6 + Math.random() * 6, life: 0, shape: Math.random() < 0.7 ? 0 : 1,
        });
      }
    }
  };
  const CYCLE = 4500 + 2000; // bursts and fall, then a 2s pause
  let round = -1, second = true;
  return loop(canvas, (ctx, t0) => {
    const t = t0 - delay;
    if (t < 0) return true;
    const r = Math.floor(t / CYCLE), inRound = t - r * CYCLE;
    if (r !== round) { round = r; second = false; fire(110); }
    if (!second && inRound > 650) { second = true; fire(70); }
    for (const p of ps) {
      p.vy += 0.32; p.vx *= 0.99; p.vy *= 0.99; p.x += p.vx; p.y += p.vy; p.r += p.vr; p.life++;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); ctx.fillStyle = p.c;
      ctx.globalAlpha = p.c === "#FFFFFF" ? 0.95 : 1;
      if (p.shape) { ctx.beginPath(); ctx.arc(0, 0, p.s / 2.4, 0, 7); ctx.fill(); } else ctx.fillRect(-p.s / 2, -p.s / 4, p.s, p.s / 2);
      ctx.restore();
    }
    // Drop pieces that have fallen off screen, so a card left open stays light.
    if (ps.length > 600) ps.splice(0, ps.length, ...ps.filter((p) => p.vy < 0 || p.y < innerHeight + 40));
    return true;
  });
}

function star(ctx: CanvasRenderingContext2D, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = (i * Math.PI) / 5 - Math.PI / 2, rr = i % 2 ? r * 0.45 : r;
    ctx.lineTo(Math.cos(a) * rr, Math.sin(a) * rr);
  }
  ctx.closePath();
  ctx.fill();
}

/** One round of fireworks (about 4s), and gold stars that keep falling until the card is closed (Peter, 2026-11-04). */
function fireworksThenStars(canvas: HTMLCanvasElement, delay = 0) {
  const colors = ["#F5C542", "#F5C542", "#FFE08A", "#2654F9", "#9DB8FF", "#E10600", "#FFFFFF"];
  const sparks: P[] = [];
  const stars: P[] = [];
  let nextBurst = 0;
  const burst = () => {
    const cx = innerWidth * (0.15 + Math.random() * 0.7), cy = innerHeight * (0.12 + Math.random() * 0.35);
    const c1 = colors[(Math.random() * colors.length) | 0], c2 = colors[(Math.random() * colors.length) | 0];
    for (let i = 0; i < 70; i++) {
      const a = (i / 70) * Math.PI * 2, sp = 2.5 + Math.random() * 3.5;
      sparks.push({ x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, r: 0, vr: 0, c: i % 2 ? c1 : c2, s: 2 + Math.random() * 1.5, life: 0, shape: 0 });
    }
  };
  const SHOW = 4200;
  return loop(canvas, (ctx, t0) => {
    const t = t0 - delay;
    if (t < 0) return true;
    if (t < SHOW && t >= nextBurst) { burst(); nextBurst = t + 380 + Math.random() * 320; }
    if (t > 2600 && Math.random() < 0.18) {
      stars.push({ x: Math.random() * innerWidth, y: -20, vx: (Math.random() - 0.5) * 0.4, vy: 0.9 + Math.random() * 1.1, r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.03, c: Math.random() < 0.8 ? "#F5C542" : "#FFE08A", s: 5 + Math.random() * 6, life: 0, shape: 0 });
    }
    for (const p of sparks) {
      if (p.life > 75) continue;
      p.vy += 0.045; p.vx *= 0.975; p.vy *= 0.975; p.x += p.vx; p.y += p.vy; p.life++;
      ctx.globalAlpha = Math.max(0, 1 - p.life / 75); ctx.fillStyle = p.c;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, 7); ctx.fill();
    }
    for (const p of stars) {
      p.x += p.vx + Math.sin((t / 600) + p.r) * 0.3; p.y += p.vy; p.r += p.vr;
      ctx.globalAlpha = 0.9; ctx.fillStyle = p.c;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r); star(ctx, p.s); ctx.restore();
    }
    ctx.globalAlpha = 1;
    // Drop finished sparks and stars that fell off screen, so a card left open stays light.
    if (sparks.length > 400) sparks.splice(0, sparks.length, ...sparks.filter((p) => p.life <= 75));
    if (stars.length > 200) stars.splice(0, stars.length, ...stars.filter((p) => p.y < innerHeight + 20));
    return true;
  });
}

function heart(ctx: CanvasRenderingContext2D, r: number) {
  ctx.beginPath();
  ctx.moveTo(0, r * 0.35);
  ctx.bezierCurveTo(-r * 1.1, -r * 0.35, -r * 0.45, -r * 1.05, 0, -r * 0.45);
  ctx.bezierCurveTo(r * 0.45, -r * 1.05, r * 1.1, -r * 0.35, 0, r * 0.35);
  ctx.closePath();
  ctx.fill();
}

/** PA of the Month: blue, red and white hearts and gold stars rising and swaying, endlessly until closed (Peter, 2026-11-04). */
function risingHearts(canvas: HTMLCanvasElement, delay = 0) {
  const colors = ["#1600FF", "#E10600", "#2654F9", "#FFFFFF", "#9D050F", "#F5C542"];
  const ps: P[] = [];
  return loop(canvas, (ctx, t0) => {
    const t = t0 - delay;
    if (t < 0) return true;
    // A quick first wave, then a steady stream.
    const rate = t < 900 ? 0.9 : 0.28;
    if (Math.random() < rate) {
      const isStar = Math.random() < 0.3;
      ps.push({
        x: Math.random() * innerWidth, y: innerHeight + 30, vx: (Math.random() - 0.5) * 0.6, vy: -(1.1 + Math.random() * 1.6),
        r: Math.random() * 6, vr: (Math.random() - 0.5) * 0.02, c: isStar ? "#F5C542" : colors[(Math.random() * 5) | 0],
        s: 8 + Math.random() * 12, life: 0, shape: isStar ? 1 : 0,
      });
    }
    for (const p of ps) {
      p.x += p.vx + Math.sin(t / 700 + p.r) * 0.5; p.y += p.vy; p.life++;
      const fade = Math.min(1, p.life / 30, Math.max(0, (p.y + 40) / (innerHeight * 0.35)));
      ctx.globalAlpha = fade * 0.95; ctx.fillStyle = p.c;
      ctx.save(); ctx.translate(p.x, p.y);
      if (p.shape) { ctx.rotate(p.r + p.life * p.vr); star(ctx, p.s * 0.6); } else heart(ctx, p.s);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    if (ps.length > 300) ps.splice(0, ps.length, ...ps.filter((p) => p.y > -40));
    return true;
  });
}
